// @flow
import { ApiClient, EvaluationApi, EvaluationBatchRequest } from 'flagr';
import { find, propEq, curry } from 'ramda';
import { createSlice } from '@reduxjs/toolkit';
import type { User } from './auth';
import config from '../../config/config';
import { notify } from '../helpers/BugsnagClient';

const debug = require('debug')('jp:stores:features');

const LOAD = 'features/LOAD';
const LOAD_SUCCESS = 'features/LOAD_SUCCESS';
const LOAD_FAIL = 'features/LOAD_FAIL';

type Flag = {| key: string, name: string |};
type Flags = Array<Flag>;

export type Feature = {|
  key: string,
  enabled: boolean,
  segmentID?: ?number,
  variantID?: ?number,
  variantKey?: ?string,
  variantAttachment?: ?any
|};

type RawFeature = {|
  flagID: number,
  flagKey: string,
  segmentID: ?number,
  variantID: ?number,
  variantKey: ?string,
  variantAttachment: any,
  evalContext: {
    entityType: string,
    flagID: number,
    entityID: string,
    entityContext: any
  },
  timestamp: string,
  flagSnapshotID: number,
  evalDebugLog: any
|};

export type State = {|
  loaded: boolean,
  loading: boolean,
  features: Array<Feature>
|};

const FLAGS: Flags = [
  {
    key: 'jp_web_disable_walking_times_display',
    name: 'jp_web_disable_walking_times_display'
  },
  {
    key: 'home_banner',
    name: 'home_banner'
  },
  {
    key: 'google_recaptcha_use_v2',
    name: 'google_recaptcha_use_v2'
  },
  {
    key: 'checkout_banner',
    name: 'checkout_banner'
  },
  {
    key: 'disable_jp_web_google_pay',
    name: 'disable_jp_web_google_pay'
  },
  {
    key: 'disable_jp_web_apple_pay',
    name: 'disable_jp_web_apple_pay'
  },
  {
    key: 'disable_jp_web_apple_sign_in',
    name: 'disable_jp_web_apple_sign_in'
  },
  {
    key: 'fancy_banner',
    name: 'fancy_banner'
  },
  {
    key: 'preload_assets',
    name: 'preload_assets'
  },
  {
    key: 'enable_3ds2',
    name: 'enable_3ds2'
  },
  {
    key: 'jp_web_app_awareness',
    name: 'jp_web_app_awareness'
  },
  {
    key: 'jp_web_temp_default_prebook',
    name: 'jp_web_temp_default_prebook'
  },
  {
    key: 'cookie_consent',
    name: 'cookie_consent'
  },
  {
    key: 'hide_srp_images',
    name: 'hide_srp_images'
  },
  {
    key: 'ev_listing_availability_info',
    name: 'ev_listing_availability_info'
  },
  {
    key: 'ev_signup',
    name: 'ev_signup'
  },
  {
    key: 'show_apps_flyer',
    name: 'show_apps_flyer'
  },
  {
    key: 'disable_jp_web_insurance_3ds2',
    name: 'disable_jp_web_insurance_3ds2'
  },
  {
    key: 'disable_jp_web_extend_3ds2',
    name: 'disable_jp_web_extend_3ds2'
  },
  {
    key: 'enable_jp_web_new_monthly_checkout',
    name: 'enable_jp_web_new_monthly_checkout'
  },
  {
    key: 'disable_jp_web_season_ticket_3ds2',
    name: 'disable_jp_web_season_ticket_3ds2'
  },
  {
    key: 'enable_jp_web_instant_book',
    name: 'enable_jp_web_instant_book'
  },
  {
    key: 'enable_jp_new_fixed_checkout',
    name: 'enable_jp_new_fixed_checkout'
  },
  {
    key: 'enable_cancel_booking_received',
    name: 'enable_cancel_booking_received'
  },
  {
    key: 'enable_new_listing_status',
    name: 'enable_new_listing_status'
  },
  {
    key: 'enable_monthly_type_bookings',
    name: 'enable_monthly_type_bookings'
  },
  {
    key: 'national_mourning',
    name: 'national_mourning'
  },
  {
    key: 'enable_new_listing_deactivation_flow',
    name: 'enable_new_listing_deactivation_flow'
  },
  {
    key: 'react18',
    name: 'react18'
  },
  {
    key: 'enable_jp_web_route_super_user',
    name: 'enable_jp_web_route_super_user'
  },
  {
    key: 'enable_jp_web_route_listing-onboarding',
    name: 'enable_jp_web_route_listing-onboarding'
  },
  {
    key: 'enable_srp_quality_markers',
    name: 'enable_srp_quality_markers'
  },
  {
    key: 'new_so_onboarding_experiment',
    name: 'new_so_onboarding_experiment'
  },
  {
    key: 'new_booking_details_page',
    name: 'new_booking_details_page'
  }
];

const flagKeys = FLAGS.map((flag: Flag) => flag.key);

const findBy = curry((propName: string, propValue: any) =>
  find(propEq(propName, propValue))
);
const findFlagByKey = findBy('key');
const findFeatureByKey = findBy('key');

const isRawFeatureEnabled = (feature: RawFeature): boolean =>
  feature.segmentID !== undefined &&
  feature.variantID !== undefined &&
  feature.variantKey !== undefined;

export const initialState: State = {
  loaded: false,
  loading: false,
  features: []
};

type GlobalState = { features: State };

type Action =
  | {| type: typeof LOAD |}
  | {| type: typeof LOAD_SUCCESS, payload: any |}
  | {| type: typeof LOAD_FAIL, payload: any |}
  | {|
      types: Array<string>,
      params?: any,
      promise: (?ApiClient) => Promise<{}>
    |};

const sliceName = 'features';

const featuresSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {},
  extraReducers: {
    [LOAD](state: State): State {
      debug(LOAD);
      return { ...state, loading: true };
    },
    [LOAD_SUCCESS](state: State, action): State {
      debug(LOAD_SUCCESS, action);
      if (!action.payload && !action.payload.evaluationResults) {
        return state;
      }

      const features = action.payload.evaluationResults
        .map((feature: RawFeature): ?Feature => {
          if (!config.isProduction) {
            debug(feature.flagID, feature.evalDebugLog);
          }
          const flag: Flag = findFlagByKey(feature.flagKey)(FLAGS);
          if (!flag) {
            return false;
          }
          return {
            key: flag.name,
            enabled: isRawFeatureEnabled(feature),
            variantKey: feature.variantKey,
            variantID: feature.variantID,
            variantAttachment: feature.variantAttachment,
            segmentID: feature.segmentID
          };
        })
        .filter((elem) => typeof elem === 'object');

      const newFeatureKeys = features.map((feature) => feature.key);

      const existingFeaturesWithDuplicatesRemoved = [...state.features].filter(
        (feature) => newFeatureKeys.indexOf(feature.key) === -1
      );

      return {
        ...state,
        loading: false,
        loaded: true,
        features: [existingFeaturesWithDuplicatesRemoved, ...features]
      };
    },
    [LOAD_FAIL](state: State, action): State {
      debug(LOAD_FAIL, action);
      return { ...state, loading: false, loaded: false };
    }
  }
});

export default featuresSlice;

export { default as isFeatureEnabled } from '../helpers/isFeatureEnabled';

export function getFeatureFromState(
  features: State,
  featureName: string
): ?Feature {
  return findFeatureByKey(featureName)(features.features);
}

const buildBatchRequest = (identifier, userLoggedIn, user, partnerName) => {
  debug(`Using identifier: ${identifier}`);

  const entityContext = {
    userLoggedIn: Boolean(userLoggedIn).toString(),
    identifier,
    userId: user?.id,
    isSuperuser: user?.isSuperuser,
    isAdmin: user?.isAdmin,
    partnerName,
    isPartnerSite: partnerName !== '',
    apiHost: config.apiHost,
    newSoOnboarding: user?.hasIncompleteListings
  };

  debug('Using entity context', entityContext);

  const evalRequest = new EvaluationBatchRequest([
    {
      entityId: identifier,
      entityType: 'user',
      entityContext
    }
  ]);
  evalRequest.flagKeys = flagKeys;
  if (!config.isProduction) {
    evalRequest.enableDebug = true;
  }
  return evalRequest;
};

export function load(user: ?User, entityId: string, partnerName) {
  debug(`Using basePath: ${config.featureApiEndpoint}`);

  const apiClient = new ApiClient();
  apiClient.basePath = config.featureApiEndpoint;
  apiClient.timeout = config.isDevelopment ? 6000 : 750;

  const apiInstance = new EvaluationApi(apiClient);

  let userLoggedIn = false;
  const identifier = entityId;
  if (user) {
    userLoggedIn = !!user.id;
  }

  return (dispatch: (Action) => void, getState: () => GlobalState) => {
    if (config.isTesting) {
      return Promise.resolve();
    }

    if (__CYPRESS__) {
      dispatch({
        type: LOAD_FAIL
      });
      return Promise.resolve();
    }

    if (!getState().features.loading && !getState().features.loaded) {
      return dispatch({
        types: [LOAD, LOAD_SUCCESS, LOAD_FAIL],
        promise: () =>
          new Promise((resolve, reject) => {
            apiInstance.postEvaluationBatch(
              buildBatchRequest(identifier, userLoggedIn, user, partnerName),
              (error, data) => {
                if (error) {
                  notify(error, {});
                  reject(new Error('Error getting features from FEATURE API'));
                } else {
                  resolve({ ...data });
                }
              }
            );
          })
      });
    }
    return Promise.resolve(getState().features.features);
  };
}
